import { DownloadService } from "../../services/download-service";
import { PartnerPhoto, StoreType } from "../../services/storageService";
import { Guid } from "../../utils/guid";
import { getCookie } from "../../utils/cookie";
import { waitAsync } from "../../utils/wait";
import { Downloader } from "./downloader";
import { FlowType } from "./upload";

export interface IDownloadStatusResult {
	readonly fileId: string;
	readonly status: number;
	readonly info: string;
	readonly originalFileName: string;
}

export interface IPartnerPhotoData {
	/** Mediastore file Id*/
	readonly Id?: string;
	/** Partner ref string*/
	readonly Code: string;
	readonly ImageFilename: string;
	readonly StoreType: StoreType;
	readonly ImageUrl: string;
}

export enum DownloadItemStates {
	WaitingForDownload = 0,
	Downloaded = 1,
	DownloadError = 2,
	Imported = 3,
	ImportError = 4,
	NotFound = 5,
}

/** For communication with client download and the core.downloader */
export interface IDownloadItem {
	/** eXpress mediastore file id*/
	readonly mediastoreFileId: string;
	/** Partner ref id*/
	readonly photoId: string;
	readonly name: string;
}

export interface IDownloadQueueItem {
	state: DownloadItemStates;
	errorMessage?: string;
	downloadedItem?: IDownloadItem;
	itemToDownload: PartnerPhoto;
}

export class Download {
	private downloader: Downloader;
	private downloadService: DownloadService;
	private downloadQueue: IDownloadQueueItem[];

	constructor(token: string, productCategoryGroupCode: string, downloader: Downloader) {
		this.downloader = downloader;
		this.downloadQueue = [];
		this.downloadService = new DownloadService(token, productCategoryGroupCode);
	}

	public async addToQueueAsync(photos: PartnerPhoto[], flowType: FlowType): Promise<IDownloadQueueItem[]> {
		const sessionCreated = await this.createSessionAsync(photos, flowType);
		if (!sessionCreated) {
			return Promise.reject(new Error("Download session could not be created on server."));
		}
		return await this.waitForAllDownloadsAsync();
	}

	private async createSessionAsync(photos: PartnerPhoto[], flowType: FlowType): Promise<boolean> {
		const downloadQueueItems: IDownloadQueueItem[] = [];
		const partnerPhotoDatas: IPartnerPhotoData[] = [];
		photos.forEach((photo) => {
			const partnerPhotoData: IPartnerPhotoData = {
				Id: Guid.newGuid(),
				Code: photo.code,
				ImageFilename: photo.name,
				ImageUrl: photo.downloadUrl,
				StoreType: photo.storeType,
			};

			const downloadQueueItem: IDownloadQueueItem = {
				state: DownloadItemStates.WaitingForDownload,
				errorMessage: undefined,
				itemToDownload: photo,
				downloadedItem: {
					photoId: partnerPhotoData.Code, // is partner ref string
					name: partnerPhotoData.ImageFilename,
					mediastoreFileId: partnerPhotoData.Id,
				},
			};

			downloadQueueItems.push(downloadQueueItem);
			partnerPhotoDatas.push(partnerPhotoData);
		});

		// Create Session
		const sessionCreated = await this.downloadService.createDownloadSessionForPartnerPhotosAsync(
			partnerPhotoDatas,
			window.context.pointOfSaleId,
			getCookie("accid"),
			window.context.language,
			flowType,
		);

		if (sessionCreated) downloadQueueItems.forEach((dqi) => this.downloadQueue.push(dqi));

		return sessionCreated;
	}

	/** Check if already downloaded, imported or failted */
	private async checkDownloadStatusAsync(): Promise<IDownloadQueueItem[] | null> {
		const fileIdsToCheck = this.downloadQueue
			.filter(
				(i) =>
					i.state !== DownloadItemStates.DownloadError &&
					i.state !== DownloadItemStates.Imported &&
					i.state !== DownloadItemStates.ImportError &&
					i.state !== DownloadItemStates.NotFound,
			)
			.filter((i) => i.downloadedItem)
			.map((item) => item.downloadedItem!.mediastoreFileId);

		const result = await this.downloadService.downloadPartnerCheckStatusFilesAsync(fileIdsToCheck, getCookie("accid"));
		const itemsChanged: IDownloadQueueItem[] = [];
		result.forEach((resultItem: IDownloadStatusResult) => {
			const queueItems = this.downloadQueue.filter((qi) => qi.downloadedItem?.mediastoreFileId === resultItem.fileId);
			if (Array.isArray(queueItems) && queueItems.length > 0) {
				const queueItem = queueItems[0];
				const newState = resultItem.status;
				if (newState !== queueItem.state) {
					queueItem.state = newState;
					itemsChanged.push(queueItem);
					if (resultItem.info) {
						queueItem.errorMessage =
							"Photo '" +
							queueItem.itemToDownload.name +
							"' (id:" +
							(queueItem.downloadedItem?.mediastoreFileId || "NA") +
							") could not be not be downloaded or imported at SmartPhoto. ErrorMessage: " +
							resultItem.info;
					}
				}
			}
		});
		if (itemsChanged.length) {
			this.downloader.handleDownloadItem(this.downloadQueue.length, result.length);
		}
		if (this.hasUncompletedDownloads()) return null;

		return this.downloadQueue;
	}

	private hasUncompletedDownloads(): boolean {
		return this.downloadQueue.some(
			(i: IDownloadQueueItem) =>
				(i.state === DownloadItemStates.WaitingForDownload || i.state === DownloadItemStates.Downloaded) &&
				(!i.errorMessage || i.errorMessage.length === 0),
		);
	}

	/** Check to close session */

	private async waitForAllDownloadsAsync(delay?: number): Promise<IDownloadQueueItem[]> {
		let del = 200;
		if (delay) {
			const check = delay * 1.1;
			del = Math.round(check);
			if (delay > 2000) del = 2000;
		}

		const files = await this.checkDownloadStatusAsync();
		if (files) return Promise.resolve(files);

		await waitAsync(del);
		return await this.waitForAllDownloadsAsync(del);
	}
}
